import { BigNumber } from 'ethers';
import { parseEther, formatEther } from 'ethers/lib/utils';
import { useMemo } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { formatDollarValue } from '../../utils/general';
import { TextContainer } from '../Container/text';
import useMarkets from '../../contexts/MarketsContext';
import { Label3, Value5 } from '../Text';
import { Tooltip } from '../Tooltip';

const Container = styled(TextContainer)`
  box-sizing: border-box;
  margin: 1.5rem 0rem;
  width: 100%;
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem;
  grid-row-gap: 0.5rem;
  grid-column-gap: 1.5rem;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const Label = styled(Label3)<{ disabled: boolean }>`
  color: ${({ theme, disabled }) => (disabled ? theme.gray3 : theme.gray4)};
  margin: 0;
`;

const Value = styled(Value5)<{ disabled: boolean }>`
  ${({ theme, disabled }) => (disabled ? `color: ${theme.bg5}` : '')}
  margin: 0;
  margin-left: auto;
`;

const TooltipContainer = styled.div`
  margin-top: 0.125rem;
  margin-left: 0.25rem;
`;

interface EntryInfoProps {
  disabled?: boolean;
  entryPrice?: BigNumber;
  priceImpact?: BigNumber;
  liquidationPrice?: BigNumber;
  fee?: BigNumber;
}

export function EntryInfo({
  entryPrice,
  priceImpact,
  liquidationPrice,
  fee,
  disabled = false,
}: EntryInfoProps) {
  const { t } = useTranslation();
  const { selectedMarket } = useMarkets();

  const priceImpactString = useMemo<string>(() => {
    const percentImpact = priceImpact?.mul(100);
    if (!percentImpact) return '-';
    if (percentImpact.abs().lt(parseEther('0.01'))) return '< 0.01%';
    return `${Number(formatEther(percentImpact.abs())).toFixed(2)}%`;
  }, [priceImpact]);

  return (
    <Container disabled={disabled}>
      <Cell>
        <Label disabled={disabled}>{t('Entry price')}</Label>
        <Value disabled={disabled}>
          {disabled || entryPrice === undefined
            ? '-'
            : Number(formatEther(entryPrice)).toFixed(selectedMarket?.displayDecimals)}
        </Value>
      </Cell>
      <Cell>
        <Label disabled={disabled}>{t('Price impact')}</Label>
        <Value disabled={disabled} value={priceImpact?.gt(parseEther('0.005')) ? -1 : 0}>
          {disabled ? '-' : priceImpactString}
        </Value>
      </Cell>
      <Cell>
        <Label disabled={disabled}>{t('Liquidation price')}</Label>
        <Value disabled={disabled}>
          {disabled || !liquidationPrice || liquidationPrice?.lt(0)
            ? '-'
            : Number(formatEther(liquidationPrice)).toFixed(selectedMarket?.displayDecimals)}
        </Value>
      </Cell>
      <Cell>
        <Label disabled={disabled}>{t('Fee')}</Label>
        <TooltipContainer>
          <Tooltip size="s" position="top" offset={0} disabled={disabled}>
            {t('Distributed to insurance fund and liquidity providers')}
          </Tooltip>
        </TooltipContainer>
        <Value disabled={disabled}>
          {disabled || !fee ? '-' : formatDollarValue(fee, 18, false, 2)}
        </Value>
      </Cell>
    </Container>
  );
}

EntryInfo.defaultProps = {
  disabled: false,
  entryPrice: undefined,
  priceImpact: undefined,
  liquidationPrice: undefined,
  fee: undefined,
};
